import { apiClient } from './request';
import {
  create,
  fromJson,
  toJson,
  type DescMessage,
  type JsonValue,
  type MessageShape,
} from '@bufbuild/protobuf';
import type {
  Channel,
  ChannelAccount,
  ChannelUserBinding,
} from '@/proto/agentplatform/v1/channel_pb';
import type {
  FeishuRegisterStatus,
  StartFeishuRegisterRequest,
} from '@/proto/agentplatform/httpv2/harness_pb';
import {
  ListChannelsResponseSchema,
  ListChannelSessionsResponseSchema,
  ListChannelUserBindingsResponseSchema,
  GetChannelSessionRequestSchema,
  GetChannelSessionResponseSchema,
  UpdateChannelSessionRequestSchema,
  UpdateChannelSessionResponseSchema,
  DeleteChannelSessionRequestSchema,
  DeleteChannelSessionResponseSchema,
  GetChannelUserBindingRequestSchema,
  GetChannelUserBindingResponseSchema,
  UpdateChannelUserBindingRequestSchema,
  UpdateChannelUserBindingResponseSchema,
  DeleteChannelUserBindingRequestSchema,
  DeleteChannelUserBindingResponseSchema,
  StartFeishuRegisterRequestSchema,
  StartFeishuRegisterResponseSchema,
  GetFeishuRegisterStatusRequestSchema,
  GetFeishuRegisterStatusResponseSchema,
} from '@/proto/agentplatform/httpv2/harness_pb';
import {
  CreateChannelAccountRequestSchema,
  CreateChannelAccountResponseSchema,
  CheckChannelAccountNameRequestSchema,
  CheckChannelAccountNameResponseSchema,
  DeleteChannelAccountRequestSchema,
  DeleteChannelAccountResponseSchema,
  DisableChannelAccountRequestSchema,
  DisableChannelAccountResponseSchema,
  EnableChannelAccountRequestSchema,
  EnableChannelAccountResponseSchema,
  GetChannelAccountRequestSchema,
  GetChannelAccountResponseSchema,
  ListChannelAccountsRequestSchema,
  ListChannelAccountsResponseSchema,
  UpdateChannelAccountRequestSchema,
  UpdateChannelAccountResponseSchema,
  type CheckChannelAccountNameRequest,
  type CreateChannelAccountRequest,
  type CreatedBridgeToken,
  type ListChannelAccountsData,
  type ListChannelAccountsRequest,
  type UpdateChannelAccountRequest,
} from '@/proto/agentplatform/httpv2/channel_pb';
import type { ListRequest } from '@/proto/agentplatform/httpv2/common_pb';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import type { ChannelSession } from '@/proto/agentplatform/v1/channel_pb';

/**
 * 拆 httpv2 成功包络取业务 data。envelope 的业务错误已由 apiClient 响应拦截器
 * 统一识别并 reject 成 ApiError（见 request.ts / errors.ts），这里只处理成功分支。
 */
async function sendV2<TResponse extends DescMessage, TData>(
  path: string,
  body: JsonValue,
  successSchema: TResponse,
  pickData: (resp: MessageShape<TResponse>) => TData | undefined
): Promise<TData> {
  const responseObject = await apiClient.post<JsonValue>(path, body);
  const raw = responseObject.data;
  const wrapped = fromJson(successSchema, raw);
  const data = pickData(wrapped);
  if (data === undefined || data === null) {
    throw new Error('empty response data');
  }
  return data;
}

/** 空 envelope 的成功响应（Delete 无 data 字段）；只做格式校验。 */
async function sendV2Void<TResponse extends DescMessage>(
  path: string,
  body: JsonValue,
  successSchema: TResponse
): Promise<void> {
  const responseObject = await apiClient.post<JsonValue>(path, body);
  fromJson(successSchema, responseObject.data); // 仅做格式校验
}

export const channelApi = {
  listChannels: async (params: ListRequest): Promise<{ data: Channel[]; total: number }> =>
    sendV2(
      '/v2/ListChannels',
      toJson(ListRequestSchema, params),
      ListChannelsResponseSchema,
      (r) => ({ data: r.data?.items ?? [], total: r.data?.total ?? 0 })
    ),

  listChannelAccounts: async (
    params: ListChannelAccountsRequest
  ): Promise<ListChannelAccountsData> =>
    sendV2(
      '/v2/ListChannelAccounts',
      toJson(ListChannelAccountsRequestSchema, params),
      ListChannelAccountsResponseSchema,
      (r) => r.data
    ),

  /**
   * 返回创建后的账号 (`account`) 及可选的 bridge token 明文列表 (`bridgeTokens`)。
   * 只有当请求中带非空 `bridgeTokens` 且 type=BRIDGE 时后端才会返回明文，且每条仅一次。
   * 空数组等同于「不创建 token」。
   */
  createChannelAccount: async (
    data: CreateChannelAccountRequest
  ): Promise<{ account: ChannelAccount; bridgeTokens: CreatedBridgeToken[] }> => {
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/CreateChannelAccount',
      toJson(CreateChannelAccountRequestSchema, data)
    );
    // envelope 业务错误已在拦截器 reject，这里只解成功包络。
    const wrapped = fromJson(CreateChannelAccountResponseSchema, responseObject.data);
    if (!wrapped.data) {
      throw new Error('empty response data');
    }
    return { account: wrapped.data, bridgeTokens: wrapped.bridgeTokens ?? [] };
  },

  /** 创建/改名前预检：名称在「租户 + 渠道类型」范围内是否可用（true = 未占用）。 */
  checkName: async (data: CheckChannelAccountNameRequest): Promise<boolean> =>
    sendV2(
      '/v2/CheckChannelAccountName',
      toJson(CheckChannelAccountNameRequestSchema, data),
      CheckChannelAccountNameResponseSchema,
      (r) => r.data?.available
    ),

  getChannelAccount: async (accountId: string): Promise<ChannelAccount> =>
    sendV2(
      '/v2/GetChannelAccount',
      toJson(
        GetChannelAccountRequestSchema,
        create(GetChannelAccountRequestSchema, { id: accountId })
      ),
      GetChannelAccountResponseSchema,
      (r) => r.data
    ),

  updateChannelAccount: async (data: UpdateChannelAccountRequest): Promise<ChannelAccount> =>
    sendV2(
      '/v2/UpdateChannelAccount',
      toJson(UpdateChannelAccountRequestSchema, data),
      UpdateChannelAccountResponseSchema,
      (r) => r.data
    ),

  deleteChannelAccount: async (accountId: string): Promise<void> =>
    sendV2Void(
      '/v2/DeleteChannelAccount',
      toJson(
        DeleteChannelAccountRequestSchema,
        create(DeleteChannelAccountRequestSchema, { id: accountId })
      ),
      DeleteChannelAccountResponseSchema
    ),

  enableChannelAccount: async (accountId: string): Promise<ChannelAccount> =>
    sendV2(
      '/v2/EnableChannelAccount',
      toJson(
        EnableChannelAccountRequestSchema,
        create(EnableChannelAccountRequestSchema, { id: accountId })
      ),
      EnableChannelAccountResponseSchema,
      (r) => r.data
    ),

  disableChannelAccount: async (accountId: string): Promise<ChannelAccount> =>
    sendV2(
      '/v2/DisableChannelAccount',
      toJson(
        DisableChannelAccountRequestSchema,
        create(DisableChannelAccountRequestSchema, { id: accountId })
      ),
      DisableChannelAccountResponseSchema,
      (r) => r.data
    ),

  // account_id 过滤通过共享 ListRequest.filter 透传。
  listChannelSessions: async (
    params: ListRequest
  ): Promise<{ data: ChannelSession[]; total: number }> =>
    sendV2(
      '/v2/ListChannelSessions',
      toJson(ListRequestSchema, params),
      ListChannelSessionsResponseSchema,
      (r) => ({ data: r.data?.items ?? [], total: r.data?.total ?? 0 })
    ),

  getChannelSession: async (sessionId: string): Promise<ChannelSession> =>
    sendV2(
      '/v2/GetChannelSession',
      toJson(
        GetChannelSessionRequestSchema,
        create(GetChannelSessionRequestSchema, { id: sessionId })
      ),
      GetChannelSessionResponseSchema,
      (r) => r.data
    ),

  updateChannelSession: async (sessionId: string, data: ChannelSession): Promise<ChannelSession> =>
    sendV2(
      '/v2/UpdateChannelSession',
      toJson(
        UpdateChannelSessionRequestSchema,
        create(UpdateChannelSessionRequestSchema, { id: sessionId, session: data })
      ),
      UpdateChannelSessionResponseSchema,
      (r) => r.data
    ),

  deleteChannelSession: async (sessionId: string): Promise<void> =>
    sendV2Void(
      '/v2/DeleteChannelSession',
      toJson(
        DeleteChannelSessionRequestSchema,
        create(DeleteChannelSessionRequestSchema, { id: sessionId })
      ),
      DeleteChannelSessionResponseSchema
    ),

  // account_id 过滤通过共享 ListRequest.filter 透传。
  listChannelUserBindings: async (
    params: ListRequest
  ): Promise<{ data: ChannelUserBinding[]; total: number }> =>
    sendV2(
      '/v2/ListChannelUserBindings',
      toJson(ListRequestSchema, params),
      ListChannelUserBindingsResponseSchema,
      (r) => ({ data: r.data?.items ?? [], total: r.data?.total ?? 0 })
    ),

  getChannelUserBinding: async (id: string): Promise<ChannelUserBinding> =>
    sendV2(
      '/v2/GetChannelUserBinding',
      toJson(
        GetChannelUserBindingRequestSchema,
        create(GetChannelUserBindingRequestSchema, { id })
      ),
      GetChannelUserBindingResponseSchema,
      (r) => r.data
    ),

  deleteChannelUserBinding: async (id: string): Promise<void> =>
    sendV2Void(
      '/v2/DeleteChannelUserBinding',
      toJson(
        DeleteChannelUserBindingRequestSchema,
        create(DeleteChannelUserBindingRequestSchema, { id })
      ),
      DeleteChannelUserBindingResponseSchema
    ),

  updateChannelUserBinding: async (
    id: string,
    data: ChannelUserBinding
  ): Promise<ChannelUserBinding> =>
    sendV2(
      '/v2/UpdateChannelUserBinding',
      toJson(
        UpdateChannelUserBindingRequestSchema,
        create(UpdateChannelUserBindingRequestSchema, { id, channelUserBinding: data })
      ),
      UpdateChannelUserBindingResponseSchema,
      (r) => r.data
    ),

  // Feishu one-click register
  startFeishuRegister: async (data: StartFeishuRegisterRequest): Promise<FeishuRegisterStatus> =>
    sendV2(
      '/v2/StartFeishuRegister',
      toJson(StartFeishuRegisterRequestSchema, data),
      StartFeishuRegisterResponseSchema,
      (r) => r.data
    ),

  getFeishuRegisterStatus: async (registrationId: string): Promise<FeishuRegisterStatus> =>
    sendV2(
      '/v2/GetFeishuRegisterStatus',
      toJson(
        GetFeishuRegisterStatusRequestSchema,
        create(GetFeishuRegisterStatusRequestSchema, { registrationId })
      ),
      GetFeishuRegisterStatusResponseSchema,
      (r) => r.data
    ),
};
